<template>
  <sb-text-input
    ref="search"
    v-model:value="searchTerm"
    class="sb-filter-search"
    data-sb-automation="filter-selection-search-input"
    :placeholder="$t(placeholder)"
    :version="dsComponentDetails.version"
    @keyup.enter.stop="handleSearch"
    @focus="computeShowHelperText"
    @blur="hideHelperText"
  >
    <template #prepend>
      <i
        aria-hidden="true"
        class="sb-filter-search__icon sb-icon-search"
        data-sb-automation="filter-selection-search-input-submit-button"
        @click="handleSearch"
      />
    </template>
    <template #append>
      <i
        v-if="dsComponentDetails.version === VersionOptions.NEXT && searchTerm?.length > 0"
        aria-hidden="true"
        class="sb-icon-x"
        data-sb-automation="filter-selection-search-input-clear-button"
        @click="clear"
      />
    </template>
  </sb-text-input>
</template>

<script>
import SbTextInput from '@src/components/Input/SbTextInput.vue';
import { versionedComponentsMixin } from '@src/mixins/versionedComponents';

/**
 * This is a component to be used as the search bar for search filters.
 */
export default {
  name: 'SbFilterSearch',
  components: {
    SbTextInput,
  },
  mixins: [versionedComponentsMixin],
  inject: ['filterSearch'],
  props: {
    /**
     * Text to be used as a placeholder for the search box.
     */
    placeholder: {
      type: String,
      default: 'common.search',
    },
    /**
     * This is the length of the string that must be reached before emitting the search event.
     */
    minSearchTermLength: {
      type: Number,
      default: 0,
    },
    /**
     * A string of helper text to be used when the minSearchTermLength is not met
     */
    searchTermHelperText: {
      type: String,
      default: '',
    },
  },
  emits: [
    'search',
  ],
  data: () => ({
    searchTerm: '',
    filterSearchLock: true,
  }),
  computed: {
    minSearchTermLengthMet() {
      return this.searchTerm.length >= this.minSearchTermLength;
    },
  },
  watch: {
    minSearchTermLengthMet: {
      handler(n) {
        if (this.filterSearchLock) return;
        this.filterSearch.showHelperText = !n;
      },
    },
    searchTermHelperText: {
      immediate: true,
      handler(n) {
        this.filterSearch.helperText = n;
      },
    },
  },
  methods: {
    handleSearch() {
      if (!this.minSearchTermLengthMet) return;
      this.$emit('search', this.searchTerm);
      if (this.dsComponentDetails.version === this.VersionOptions.LEGACY) {
        this.$el.querySelector('input').blur();
      } else {
        this.searchTerm = '';
      }
    },
    clear() {
      this.searchTerm = '';
      this.$emit('update:value', this.searchTerm);
    },
    computeShowHelperText() {
      this.filterSearchLock = false;
      this.filterSearch.showHelperText = !this.minSearchTermLengthMet;
    },
    hideHelperText() {
      this.filterSearchLock = true;
      this.filterSearch.showHelperText = false;
    },
  },
};
</script>

<style scoped lang='scss'>
@import '@src/components/Filter/style/sb-filter-search.scss';
</style>
